import { AntTooltip } from '../../../components/common/AntTooltip';
import { useState } from 'react';
import { RX_RENDER_PDF, RX_RENDER_PDF_DUMMY } from '../common/RxTemplatePdfView';
import CopyRxTemplateModal from '../common/CopyRxTemplateModal';
import { DocProfileResponse } from '../../../api/fetchDoctorProfile';
import { TemplateV2 } from '../../../api/pushRenderPdfTemplateConfig';
import { TemplateConfig } from '../../../Templar/RenderPdfPrescription';
import { LocalTemplateConfig, NO_FOOTER, NO_HEADER } from '../EditTemplate';
import { notification } from 'antd';
import uploadRenderPdfImages from '../../../api/uploadRenderPdfImages';
import { RenderPdfConfig } from '../../../Templar/types';

const ClinicSelectorAndCopy: React.FC<{
    templateTypeTab: 'PRINT' | 'SEND';
    onClinicChange: (id: string) => void;
    activeClinic: string | undefined;
    docProfile: DocProfileResponse | undefined;
    saveConfigToDb: (
        rxConf: TemplateV2,
        onComplete?: ((status: boolean) => void) | undefined,
    ) => Promise<void>;
    saveRxLocalConfigInState: (rxObj: LocalTemplateConfig) => void;
    closeIsCopyToSendTemplate: () => void;
    openIsCopyToSendTemplate: () => void;
    isCopyToSendTemplate: boolean;
}> = ({
    templateTypeTab,
    onClinicChange,
    activeClinic,
    docProfile,
    saveConfigToDb,
    saveRxLocalConfigInState,
    closeIsCopyToSendTemplate,
    openIsCopyToSendTemplate,
    isCopyToSendTemplate,
}) => {
    const [isCopyLoading, setisCopyLoading] = useState(false);

    const onPrint = () => {
        (document?.getElementById?.(RX_RENDER_PDF)
            ?.children[0] as HTMLIFrameElement)?.contentWindow?.print();
    };

    const uploadCopiedHeaderImage = async (file: File, type: 'PRINT' | 'SEND') => {
        if (!docProfile?._id && !activeClinic) {
            notification.error({
                message: 'Copy failed. Doctor or Clinic id not found',
                duration: 5,
                placement: 'topRight',
            });
            return;
        }

        let newFile = new File(
            [file],
            `${docProfile?._id}-${activeClinic}-header.${file.type.split('/')[1]}`,
        );

        if (type === 'SEND') {
            newFile = new File(
                [file],
                `${docProfile?._id}-${activeClinic}-header-send.${file.type.split('/')[1]}`,
            );
        }

        const formData = new FormData();
        formData.append('header-footer', newFile);

        try {
            const data = await uploadRenderPdfImages(formData);

            if (data?.success) {
                return data.url;
            } else {
                notification.error({
                    message: 'Failed to save Header Image',
                    duration: 5,
                });
                return;
            }
        } catch (error) {
            notification.error({
                message: 'Failed to save Header Image',
                duration: 5,
            });
            console.error(error);
            return;
        }
    };

    const uploadCopiedFooterImage = async (file: File, type: 'PRINT' | 'SEND') => {
        if (!docProfile?._id && !activeClinic) {
            notification.error({
                message: 'Copy failed. Doctor or Clinic id not found',
                duration: 5,
                placement: 'topRight',
            });
            return;
        }

        let newFile = new File(
            [file],
            `${docProfile?._id}-${activeClinic}-footer.${file.type.split('/')[1]}`,
        );

        if (type === 'SEND') {
            newFile = new File(
                [file],
                `${docProfile?._id}-${activeClinic}-footer-send.${file.type.split('/')[1]}`,
            );
        }

        const formData = new FormData();
        formData.append('header-footer', newFile);

        try {
            const data = await uploadRenderPdfImages(formData);

            if (data?.success) {
                return data.url;
            } else {
                notification.error({
                    message: 'Failed to copy Footer Image',
                    duration: 5,
                });
                return;
            }
        } catch (error) {
            notification.error({
                message: 'Failed to copy Footer Image',
                duration: 5,
            });
            console.error(error);
            return;
        }
    };

    const uploadCopiedAttachmentImage = async (file: File, type: 'PRINT' | 'SEND') => {
        if (!docProfile?._id && !activeClinic) {
            notification.error({
                message: 'Copy failed. Doctor or Clinic id not found',
                duration: 5,
                placement: 'topRight',
            });
            return;
        }

        let newFile = new File(
            [file],
            `${docProfile?._id}-${activeClinic}-attachment-print.${file.type.split('/')[1]}`,
        );

        if (type === 'SEND') {
            newFile = new File(
                [file],
                `${docProfile?._id}-${activeClinic}-attachment-send.${file.type.split('/')[1]}`,
            );
        }

        const formData = new FormData();
        formData.append('header-footer', newFile);

        try {
            const data = await uploadRenderPdfImages(formData);

            if (data?.success) {
                return data.url;
            } else {
                notification.error({
                    message: 'Failed to copy Attachment Image',
                    duration: 5,
                });
                return;
            }
        } catch (error) {
            notification.error({
                message: 'Failed to copy Attachment Image',
                duration: 5,
            });
            console.error(error);
            return;
        }
    };

    const convertToBase64AndUploadHeaderImage = async (rxConfig?: TemplateV2) => {
        if (rxConfig?.header_img && rxConfig?.header_img !== NO_HEADER) {
            const base64Response = await fetch((rxConfig.header_img || '')?.split('?')?.[0]);
            const blob = await base64Response.blob();

            const file = new File([blob], 'header-footer', {
                type: 'image/png',
            });
            const url = await uploadCopiedHeaderImage(file, 'SEND');

            if (!url) {
                notification.error({
                    message: 'Copy failed. Please try again',
                    duration: 5,
                    placement: 'topRight',
                });
                return;
            }

            return url;
        }

        return;
    };

    const convertToBase64AndUploadFooterImage = async (rxConfig?: TemplateV2) => {
        if (rxConfig?.footer_img && rxConfig?.footer_img !== NO_FOOTER) {
            const base64Response = await fetch((rxConfig.footer_img || '')?.split('?')?.[0]);
            const blob = await base64Response.blob();

            const file = new File([blob], 'header-footer', {
                type: 'image/png',
            });
            const url = await uploadCopiedFooterImage(file, 'SEND');

            if (!url) {
                notification.error({
                    message: 'Copy failed. Please try again',
                    duration: 5,
                    placement: 'topRight',
                });
                return;
            }

            return url;
        }

        return;
    };

    const convertToBase64AndUploadAttachmentImage = async (rxConfig?: TemplateV2) => {
        if (rxConfig?.attachment_image) {
            const base64Response = await fetch((rxConfig.attachment_image || '')?.split('?')?.[0]);
            const blob = await base64Response.blob();

            const file = new File([blob], 'header-footer', {
                type: 'image/png',
            });
            const url = await uploadCopiedAttachmentImage(file, 'SEND');

            if (!url) {
                notification.error({
                    message: 'Copy failed. Please try again',
                    duration: 5,
                    placement: 'topRight',
                });
                return;
            }

            return url;
        }

        return;
    };

    const buildCopyConfigObject = (
        rxConfig?: TemplateConfig,
        type?: 'PRINT' | 'SEND',
        copyToClinicId?: string,
        headerImage?: string,
        footerImage?: string,
        attachmentImage?: string,
    ): RenderPdfConfig | undefined => {
        if (!docProfile?._id || !copyToClinicId || !rxConfig) {
            return;
        }

        if (type === 'SEND') {
            return {
                docid: docProfile._id,
                clinicId: copyToClinicId,
                header_img: headerImage || '',
                footer_img: footerImage || '',
                show_signature: rxConfig.show_signature,
                show_name_in_signature: rxConfig.show_name_in_signature,
                show_signature_text: rxConfig.show_signature_text,
                show_page_number: rxConfig.show_page_number,
                show_prescription_id: rxConfig.show_prescription_id,
                type: 'SEND',
                page_size: 'A4',
                margin_left: '1.27cm',
                margin_right: '1.27cm',
                header_height: headerImage
                    ? rxConfig.header_height || ''
                    : rxConfig.floating_patient_details
                    ? '5cm'
                    : '3cm',
                footer_height: footerImage ? rxConfig.footer_height || '' : '6cm',
                bullets_config: rxConfig.bullets_config,
                prescription_size: rxConfig.prescription_size,
                date_and_time: rxConfig.date_and_time,
                header_top_margin: headerImage ? rxConfig.header_top_margin : '',
                header_bottom_margin: headerImage ? rxConfig.header_bottom_margin : '',
                header_left_margin: headerImage ? rxConfig.header_left_margin : '',
                header_right_margin: headerImage ? rxConfig.header_right_margin : '',
                footer_top_margin: footerImage ? rxConfig.footer_top_margin : '',
                footer_bottom_margin: footerImage ? rxConfig.footer_bottom_margin : '',
                footer_left_margin: footerImage ? rxConfig.footer_left_margin : '',
                footer_right_margin: footerImage ? rxConfig.footer_right_margin : '',
                lab_vitals_format: rxConfig.lab_vitals_format,
                show_not_valid_for_medical_legal_purpose_message:
                    rxConfig.show_not_valid_for_medical_legal_purpose_message,
                show_eka_logo: rxConfig.show_eka_logo,
                medication_table_format: rxConfig.medication_table_format,
                advices_format: rxConfig.advices_format,
                medication_name_in_capital: rxConfig?.medication_name_in_capital,
                make_generic_name_as_primary: rxConfig?.make_generic_name_as_primary,
                symptoms_name_in_capital: rxConfig.symptoms_name_in_capital,
                diagnosis_name_in_capital: rxConfig.diagnosis_name_in_capital,
                lab_tests_name_in_capital: rxConfig.lab_tests_name_in_capital,
                symptoms_name_in_unbold: rxConfig.symptoms_name_in_unbold,
                diagnosis_name_in_unbold: rxConfig.diagnosis_name_in_unbold,
                lab_tests_name_in_unbold: rxConfig.lab_tests_name_in_unbold,
                lab_vitals_name_in_unbold: rxConfig.lab_vitals_name_in_unbold,
                medical_history_name_in_unbold: rxConfig.medical_history_name_in_unbold,
                vitals_in_unbold: rxConfig.vitals_in_unbold,
                examination_findings_name_in_unbold: rxConfig.examination_findings_name_in_unbold,
                patient_details_format: rxConfig.patient_details_format,
                referred_to_in_unbold: rxConfig.referred_to_in_unbold,
                dyform_in_unbold: rxConfig.dyform_in_unbold,
                dyform_data_layout: rxConfig.dyform_data_layout,
                dyform_heading_color: rxConfig.dyform_heading_color,
                dyform_name_color: rxConfig.dyform_name_color,
                dyform_properties_color: rxConfig.dyform_properties_color,
                patient_form_data_in_unbold: rxConfig.patient_form_data_in_unbold,
                followup_in_unbold: rxConfig.followup_in_unbold,
                date_in_unbold: rxConfig.date_in_unbold,
                patient_form_data_format: rxConfig.patient_form_data_format,
                attachment_image: attachmentImage,
                template_column_type: rxConfig.template_column_type,
                columns_config: rxConfig.columns_config || {},
                symptoms_heading_color: rxConfig.symptoms_heading_color,
                symptoms_name_color: rxConfig.symptoms_name_color,
                symptoms_properties_color: rxConfig.symptoms_properties_color,
                diagnosis_heading_color: rxConfig.diagnosis_heading_color,
                diagnosis_name_color: rxConfig.diagnosis_name_color,
                diagnosis_properties_color: rxConfig.diagnosis_properties_color,
                vitals_heading_color: rxConfig.vitals_heading_color,
                vitals_name_color: rxConfig.vitals_name_color,
                vitals_properties_color: rxConfig.vitals_properties_color,
                lab_tests_heading_color: rxConfig.lab_tests_heading_color,
                lab_tests_name_color: rxConfig.lab_tests_name_color,
                lab_tests_properties_color: rxConfig.lab_tests_properties_color,
                lab_vitals_heading_color: rxConfig.lab_vitals_heading_color,
                lab_vitals_name_color: rxConfig.lab_vitals_name_color,
                lab_vitals_properties_color: rxConfig.lab_vitals_properties_color,
                referred_to_heading_color: rxConfig.referred_to_heading_color,
                referred_to_properties_color: rxConfig.referred_to_properties_color,
                advices_heading_color: rxConfig.advices_heading_color,
                advices_properties_color: rxConfig.advices_properties_color,
                medications_table_border_color: rxConfig.medications_table_border_color,
                medications_table_title_bg_color: rxConfig.medications_table_title_bg_color,
                pmh_heading_color: rxConfig.pmh_heading_color,
                pmh_name_color: rxConfig.pmh_name_color,
                pmh_properties_color: rxConfig.pmh_properties_color,
                examinations_heading_color: rxConfig.examinations_heading_color,
                examinations_name_color: rxConfig.examinations_name_color,
                examinations_properties_color: rxConfig.examinations_properties_color,
                notes_heading_color: rxConfig.notes_heading_color,
                notes_properties_color: rxConfig.notes_properties_color,
                followup_heading_color: rxConfig.followup_heading_color,
                followup_properties_color: rxConfig.followup_properties_color,
                spacing_between_sections: rxConfig.spacing_between_sections,
                patient_form_below_border: rxConfig.patient_form_below_border,
                patient_details_in_uppercase: rxConfig.patient_details_in_uppercase,
                show_approval_details: rxConfig.show_approval_details,
                injections_name_in_capital: rxConfig.injections_name_in_capital,
                injections_heading_hide: rxConfig.injections_heading_hide,
                injections_table_heading_text: rxConfig.injections_table_heading_text,
                injections_table_title_bg_color: rxConfig.injections_table_title_bg_color,
                injections_table_border_color: rxConfig.injections_table_border_color,
                injections_table_header_color: rxConfig.injections_table_header_color,
                injections_table_title_alignment: rxConfig.injections_table_title_alignment,
                current_injections_heading_color: rxConfig.current_injections_heading_color,
                current_injections_name_color: rxConfig.current_injections_properties_color,
                current_injections_properties_color: rxConfig.current_injections_properties_color,
                injections_table_width: rxConfig.injections_table_width,
                injections_table_format: rxConfig.injections_table_format,
                opthal_heading_color: rxConfig.opthal_heading_color,
                dental_examinations_heading_color: rxConfig.dental_examinations_heading_color,
                dental_examinations_properties_color: rxConfig.dental_examinations_properties_color,
                dental_examinations_key_color: rxConfig.dental_examinations_key_color,
                dental_procedures_heading_color: rxConfig.dental_procedures_heading_color,
                dental_procedures_table_heading_color:
                    rxConfig?.dental_procedures_table_heading_color,
                dental_procedures_table_border_color:
                    rxConfig?.dental_procedures_table_border_color,
                opthal_table_heading_color: rxConfig?.opthal_table_heading_color,
                opthal_table_border_color: rxConfig?.opthal_table_border_color,
                admission_advised_heading_color: rxConfig?.admission_advised_heading_color,
                admission_advised_name_color: rxConfig?.admission_advised_name_color,
                admission_advised_properties_color: rxConfig?.admission_advised_properties_color,
                growth_chart_heading_color: rxConfig?.growth_chart_heading_color,
                growth_chart_name_color: rxConfig?.growth_chart_name_color,
                growth_chart_properties_color: rxConfig?.growth_chart_properties_color,
                care_canvas_heading_color: rxConfig?.care_canvas_heading_color,
                tabular_config: rxConfig.tabular_config,
                medication_table_heading_text: rxConfig.medication_table_heading_text,
                floating_patient_details: rxConfig.floating_patient_details,
                floating_patient_details_top_margin: rxConfig.floating_patient_details_top_margin,
                medical_history_status_display: rxConfig?.medical_history_status_display,
            };
        }

        return {
            docid: docProfile._id,
            clinicId: copyToClinicId,
            header_img: headerImage,
            footer_img: footerImage,
            show_signature: rxConfig.show_signature,
            show_name_in_signature: rxConfig.show_name_in_signature,
            show_signature_text: rxConfig.show_signature_text,
            show_page_number: rxConfig.show_page_number,
            show_prescription_id: rxConfig.show_prescription_id,
            type: 'PRINT',
            page_size: 'A4',
            margin_left: rxConfig.margin_left || '',
            margin_right: rxConfig.margin_right || '',
            header_height: rxConfig.header_height || '',
            footer_height: rxConfig.footer_height || '',
            bullets_config: rxConfig.bullets_config,
            prescription_size: rxConfig.prescription_size,
            date_and_time: rxConfig.date_and_time,
            header_top_margin: rxConfig.header_top_margin,
            header_bottom_margin: rxConfig.header_bottom_margin,
            header_left_margin: rxConfig.header_left_margin,
            header_right_margin: rxConfig.header_right_margin,
            footer_top_margin: rxConfig.footer_top_margin,
            footer_bottom_margin: rxConfig.footer_bottom_margin,
            footer_left_margin: rxConfig.footer_left_margin,
            footer_right_margin: rxConfig.footer_right_margin,
            floating_header: rxConfig.floating_header,
            floating_patient_details: rxConfig.floating_patient_details,
            floating_patient_details_top_margin: rxConfig.floating_patient_details_top_margin,
            floating_footer: rxConfig.floating_footer,
            lab_vitals_format: rxConfig.lab_vitals_format,
            show_not_valid_for_medical_legal_purpose_message:
                rxConfig.show_not_valid_for_medical_legal_purpose_message,
            show_eka_logo: rxConfig.show_eka_logo,
            medication_table_format: rxConfig.medication_table_format,
            floating_header_page_top_margin: rxConfig.floating_header_page_top_margin,
            floating_footer_page_bottom_margin: rxConfig.floating_footer_page_bottom_margin,
            advices_format: rxConfig.advices_format,
            medication_name_in_capital: rxConfig?.medication_name_in_capital,
            make_generic_name_as_primary: rxConfig?.make_generic_name_as_primary,
            symptoms_name_in_capital: rxConfig.symptoms_name_in_capital,
            diagnosis_name_in_capital: rxConfig.diagnosis_name_in_capital,
            lab_tests_name_in_capital: rxConfig.lab_tests_name_in_capital,
            symptoms_name_in_unbold: rxConfig.symptoms_name_in_unbold,
            diagnosis_name_in_unbold: rxConfig.diagnosis_name_in_unbold,
            lab_tests_name_in_unbold: rxConfig.lab_tests_name_in_unbold,
            lab_vitals_name_in_unbold: rxConfig.lab_vitals_name_in_unbold,
            medical_history_name_in_unbold: rxConfig.medical_history_name_in_unbold,
            vitals_in_unbold: rxConfig.vitals_in_unbold,
            examination_findings_name_in_unbold: rxConfig.examination_findings_name_in_unbold,
            patient_details_format: rxConfig.patient_details_format,
            referred_to_in_unbold: rxConfig.referred_to_in_unbold,
            dyform_in_unbold: rxConfig.dyform_in_unbold,
            dyform_data_layout: rxConfig.dyform_data_layout,
            dyform_heading_color: rxConfig.dyform_heading_color,
            dyform_name_color: rxConfig.dyform_name_color,
            dyform_properties_color: rxConfig.dyform_properties_color,
            patient_form_data_in_unbold: rxConfig.patient_form_data_in_unbold,
            followup_in_unbold: rxConfig.followup_in_unbold,
            date_in_unbold: rxConfig.date_in_unbold,
            patient_form_data_format: rxConfig.patient_form_data_format,
            attachment_image: attachmentImage,
            template_column_type: rxConfig.template_column_type,
            columns_config: rxConfig.columns_config || {},
            symptoms_heading_color: rxConfig.symptoms_heading_color,
            symptoms_name_color: rxConfig.symptoms_name_color,
            symptoms_properties_color: rxConfig.symptoms_properties_color,
            diagnosis_heading_color: rxConfig.diagnosis_heading_color,
            diagnosis_name_color: rxConfig.diagnosis_name_color,
            diagnosis_properties_color: rxConfig.diagnosis_properties_color,
            vitals_heading_color: rxConfig.vitals_heading_color,
            vitals_name_color: rxConfig.vitals_name_color,
            vitals_properties_color: rxConfig.vitals_properties_color,
            lab_tests_heading_color: rxConfig.lab_tests_heading_color,
            lab_tests_name_color: rxConfig.lab_tests_name_color,
            lab_tests_properties_color: rxConfig.lab_tests_properties_color,
            lab_vitals_heading_color: rxConfig.lab_vitals_heading_color,
            lab_vitals_name_color: rxConfig.lab_vitals_name_color,
            lab_vitals_properties_color: rxConfig.lab_vitals_properties_color,
            referred_to_heading_color: rxConfig.referred_to_heading_color,
            referred_to_properties_color: rxConfig.referred_to_properties_color,
            advices_heading_color: rxConfig.advices_heading_color,
            advices_properties_color: rxConfig.advices_properties_color,
            medications_table_border_color: rxConfig.medications_table_border_color,
            medications_table_title_bg_color: rxConfig.medications_table_title_bg_color,
            pmh_heading_color: rxConfig.pmh_heading_color,
            pmh_name_color: rxConfig.pmh_name_color,
            pmh_properties_color: rxConfig.pmh_properties_color,
            examinations_heading_color: rxConfig.examinations_heading_color,
            examinations_name_color: rxConfig.examinations_name_color,
            examinations_properties_color: rxConfig.examinations_properties_color,
            notes_heading_color: rxConfig.notes_heading_color,
            notes_properties_color: rxConfig.notes_properties_color,
            followup_heading_color: rxConfig.followup_heading_color,
            followup_properties_color: rxConfig.followup_properties_color,
            spacing_between_sections: rxConfig.spacing_between_sections,
            patient_form_below_border: rxConfig.patient_form_below_border,
            patient_details_in_uppercase: rxConfig.patient_details_in_uppercase,
            show_approval_details: rxConfig.show_approval_details,
            injections_name_in_capital: rxConfig.injections_name_in_capital,
            injections_heading_hide: rxConfig.injections_heading_hide,
            injections_table_heading_text: rxConfig.injections_table_heading_text,
            injections_table_title_bg_color: rxConfig.injections_table_title_bg_color,
            injections_table_border_color: rxConfig.injections_table_border_color,
            injections_table_header_color: rxConfig.injections_table_header_color,
            injections_table_title_alignment: rxConfig.injections_table_title_alignment,
            current_injections_heading_color: rxConfig.current_injections_heading_color,
            current_injections_name_color: rxConfig.current_injections_properties_color,
            current_injections_properties_color: rxConfig.current_injections_properties_color,
            injections_table_width: rxConfig.injections_table_width,
            injections_table_format: rxConfig.injections_table_format,
            opthal_heading_color: rxConfig.opthal_heading_color,
            dental_examinations_heading_color: rxConfig.dental_examinations_heading_color,
            dental_examinations_properties_color: rxConfig.dental_examinations_properties_color,
            dental_examinations_key_color: rxConfig.dental_examinations_key_color,
            dental_procedures_heading_color: rxConfig.dental_procedures_heading_color,
            dental_procedures_table_heading_color: rxConfig?.dental_procedures_table_heading_color,
            dental_procedures_table_border_color: rxConfig?.dental_procedures_table_border_color,
            opthal_table_heading_color: rxConfig?.opthal_table_heading_color,
            opthal_table_border_color: rxConfig?.opthal_table_border_color,
            admission_advised_heading_color: rxConfig?.admission_advised_heading_color,
            admission_advised_name_color: rxConfig?.admission_advised_name_color,
            admission_advised_properties_color: rxConfig?.admission_advised_properties_color,
            growth_chart_heading_color: rxConfig?.growth_chart_heading_color,
            growth_chart_name_color: rxConfig?.growth_chart_name_color,
            growth_chart_properties_color: rxConfig?.growth_chart_properties_color,
            care_canvas_heading_color: rxConfig?.care_canvas_heading_color,
            tabular_config: rxConfig.tabular_config,
            medication_table_heading_text: rxConfig.medication_table_heading_text,
            medical_history_status_display: rxConfig?.medical_history_status_display,
        };
    };

    const onCopyToSendRx = async () => {
        if (!activeClinic) {
            return;
        }
        setisCopyLoading(true);
        const doctorTemplateData = docProfile?.profile?.professional?.templates_v2;

        const rxConfig = doctorTemplateData?.find(
            (data) => data.clinicId === activeClinic && data.type === 'PRINT',
        );

        const [headerImageUrl, footerImageUrl, attachmentImageUrl] = await Promise.all([
            convertToBase64AndUploadHeaderImage(rxConfig),
            convertToBase64AndUploadFooterImage(rxConfig),
            convertToBase64AndUploadAttachmentImage(rxConfig),
        ]);

        const rxConf = buildCopyConfigObject(
            rxConfig,
            'SEND',
            activeClinic,
            headerImageUrl,
            footerImageUrl,
            attachmentImageUrl,
        );

        if (!rxConf) {
            return;
        }

        saveConfigToDb(rxConf, () => {
            saveRxLocalConfigInState({
                header_auto_adjust: false,
                footer_auto_adjust: false,
            });
            onClinicChange(activeClinic);
        });

        closeIsCopyToSendTemplate();
        setisCopyLoading(false);
    };

    const clinics = docProfile?.profile?.professional?.clinics;

    return (
        <>
            <div className="sticky pt-4 bg-neutral-9 z-10 top-0 space-y-4">
                <div className="flex items-center justify-between">
                    <p className="font-700">Select Clinic:</p>

                    <div className="flex items-center space-x-8">
                        {templateTypeTab === 'SEND' && (
                            <AntTooltip content="Copy from Print Rx Template">
                                <span
                                    onClick={openIsCopyToSendTemplate}
                                    className="h-28 w-28 flex justify-center items-center bg-darwin-neutral-100 rounded-4 cursor-pointer"
                                >
                                    <svg
                                        role="img"
                                        viewBox="0 0 12 13"
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-16 h-16 fill-darwin-primary-500"
                                    >
                                        <path d="M9 2.75V0.5H6.375C5.74219 0.5 5.25 1.01562 5.25 1.625V8.375C5.25 9.00781 5.74219 9.5 6.375 9.5H10.875C11.4844 9.5 12 9.00781 12 8.375V3.5H9.75C9.32812 3.5 9 3.17188 9 2.75ZM9.75 0.5V2.75H12L9.75 0.5ZM4.5 8.75V3.5H1.125C0.492188 3.5 0 4.01562 0 4.625V11.375C0 12.0078 0.492188 12.5 1.125 12.5H5.625C6.23438 12.5 6.75 12.0078 6.75 11.375V10.25H6C5.15625 10.25 4.5 9.59375 4.5 8.75Z"></path>
                                    </svg>
                                </span>
                            </AntTooltip>
                        )}
                        <div
                            onClick={onPrint}
                            className="h-28 w-28 flex justify-center items-center bg-darwin-neutral-100 rounded-4 cursor-pointer"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                                className="w-16 h-16 fill-darwin-primary-500"
                                // fill="red"
                            >
                                <path d="M128 0C92.7 0 64 28.7 64 64v96h64V64H354.7L384 93.3V160h64V93.3c0-17-6.7-33.3-18.7-45.3L400 18.7C388 6.7 371.7 0 354.7 0H128zM384 352v32 64H128V384 368 352H384zm64 32h32c17.7 0 32-14.3 32-32V256c0-35.3-28.7-64-64-64H64c-35.3 0-64 28.7-64 64v96c0 17.7 14.3 32 32 32H64v64c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V384zM432 248a24 24 0 1 1 0 48 24 24 0 1 1 0-48z" />
                            </svg>
                        </div>
                    </div>
                </div>
                <div className="space-y-8">
                    <div className="flex justify-between px-12 py-10 border border-neutral-5 rounded-4 text-14">
                        <select
                            className="w-full bg-transparent focus:outline-none text-neutral-black font-600 text-opacity-60 truncate cursor-pointer"
                            value={activeClinic}
                            onChange={(e) => {
                                onClinicChange(e.target.value);
                            }}
                        >
                            {clinics?.map((c) => (
                                <option key={c._id} value={c._id}>
                                    {c.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="border-b mt-4 border-darwin-neutral-400"></div>
                </div>
            </div>
            {isCopyToSendTemplate && activeClinic && docProfile && (
                <CopyRxTemplateModal
                    onCancel={closeIsCopyToSendTemplate}
                    onCopy={onCopyToSendRx}
                    docProfile={docProfile}
                    activeClinic={activeClinic}
                    isCopyLoading={isCopyLoading}
                />
            )}
        </>
    );
};

export default ClinicSelectorAndCopy;
